type SearchType = "web" | "ai";

export type SearchEntry = {
  id: string;
  query: string;
  type: SearchType;
  engineOrModel: string;
  timestamp: number;
  threadId?: string;
};

const STORAGE_PREFIX = "kourai-search-history";
const MAX_ENTRIES = 50;

function getStorageKey(workspaceId?: string | null): string {
  return workspaceId ? `${STORAGE_PREFIX}-${workspaceId}` : STORAGE_PREFIX;
}

export function getHistory(workspaceId?: string | null): SearchEntry[] {
  try {
    const raw = localStorage.getItem(getStorageKey(workspaceId));
    if (!raw) return [];
    const entries: SearchEntry[] = JSON.parse(raw);
    return entries.sort((a, b) => b.timestamp - a.timestamp);
  } catch {
    return [];
  }
}

export function addEntry(
  entry: Omit<SearchEntry, "id" | "timestamp">,
  workspaceId?: string | null,
): void {
  try {
    const history = getHistory(workspaceId);
    const filtered = history.filter(
      (h) =>
        !(
          h.query.toLowerCase() === entry.query.toLowerCase() &&
          h.type === entry.type &&
          h.engineOrModel === entry.engineOrModel
        ),
    );
    filtered.unshift({
      ...entry,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    });
    localStorage.setItem(
      getStorageKey(workspaceId),
      JSON.stringify(filtered.slice(0, MAX_ENTRIES)),
    );
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded).
    console.error("[search-history] Failed to persist search entry");
  }
}

export function clearHistory(workspaceId?: string | null): void {
  try {
    localStorage.removeItem(getStorageKey(workspaceId));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded).
    console.error("[search-history] Failed to clear history");
  }
}

/**
 * History saved while no workspace was active belongs to the Private workspace: move it under
 * that workspace's key, merged with what is already there, so it keeps showing there.
 */
export function adoptUnscopedHistory(privateWorkspaceId: string): void {
  try {
    const unscoped = getHistory(null);
    if (unscoped.length === 0) return;
    const merged = [...getHistory(privateWorkspaceId), ...unscoped]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, MAX_ENTRIES);
    localStorage.setItem(getStorageKey(privateWorkspaceId), JSON.stringify(merged));
    localStorage.removeItem(getStorageKey(null));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded).
  }
}

/**
 * Suggestions from the user's own history for what is being typed: entries
 * whose query starts with the text first, then entries with a word starting
 * with it, then any other entry containing it. Newest first within each group.
 */
export function matchHistory(entries: SearchEntry[], input: string, limit = 5): SearchEntry[] {
  const needle = input.trim().toLowerCase();
  if (!needle) return [];
  const ranked: { entry: SearchEntry; rank: number }[] = [];
  for (const entry of entries) {
    const query = entry.query.toLowerCase();
    const index = query.indexOf(needle);
    if (index === -1) continue;
    const rank = index === 0 ? 0 : /\s/.test(query[index - 1] ?? "") ? 1 : 2;
    ranked.push({ entry, rank });
  }
  return ranked
    .sort((a, b) => a.rank - b.rank || b.entry.timestamp - a.entry.timestamp)
    .slice(0, limit)
    .map((r) => r.entry);
}
